<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>
    <div class="mt-8 min-h-[14rem] flex flex-col justify-between">
      <div class="flex gap-x-5 pb-5">
        <span class="text-input">{{ $t(`${tKey}.notification`) }}</span>
        <span>{{ notificationPlan?.name }}</span>
      </div>
      <Multiselect
        v-model="formData.driver"
        :error="errors?.driver"
        class="col-start-1 col-end-2"
        :options="driverList"
        :label="$t('pages.notificationList.testModal.driver')"
        :can-deselect="false"
        :can-clear="false"
        :searchable="true"
        :min-chars="2"
        :filterResults="false"
        :delay="500"
        :resolve-on-load="true"
        :create-option="false"
        :clear-on-select="true"
        :no-options-text="$t('inputSearchString')"
      />
      <div class="w-full text-center">
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loading"
          @click="submit"
        >
          {{ $t(`${tKey}.save`) }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import Multiselect from '@utils/Multiselect.vue';
import { collOptionsProvider, type ProviderParams } from '@utils/table/hooks/provider';
import CustomButton from '@utils/CustomButton.vue';
import type { Driver, NotificationPlan } from '@/entities/Entities';

interface Props {
  open: boolean;
  notificationPlan?: NotificationPlan;
}

const emit = defineEmits(['update:open']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const tKey = 'pages.notificationList.testModal';

const { t } = useI18n();

const notify = useNotify();

const driverList = (query: string, select: any) => {
  const input = query || select.modelValue;
  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'user.fullName': input } : { id: select.modelValue },
  };
  return collOptionsProvider<Driver>('/drivers', filterQuery, (value) => ({
    label: `${value.user?.fullName} (${value.user?.email})`,
    value: value['@id'],
  }))();
};

const formData = ref();

const newFormData = () => ({
  driver: undefined,
});

formData.value = newFormData();

const {
  data: validatedData,
  errors,
  validatedFn,
  setValidation,
} = useValidation(object({
  driver: string().required(t('validation.notEmpty')),
}), formData);

function closeModal() {
  setValidation(false);
  internalOpen.value = false;
  formData.value = newFormData();
}

const [exec, { loading }] = useFetchAction();

const submit = validatedFn(async () => {
  try {
    await exec('/notification_plans/test', {
      method: 'POST',
      body: {
        driver: validatedData.value.driver,
        notificationPlan: props.notificationPlan?.['@id'],
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey}.saveSuccess`),
    });

    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped>
.translation-accordion-style {
  @apply px-6 py-5;
  @apply mb-5 ml-2;
  @apply border-l border-l-element-neutral border-b border-b-element-neutral;
}
</style>
